import {
    DeleteOutlined,
    DrawOutlined,
    EditOutlined,
    Star,
    StarBorder,
    TextFieldsOutlined,
    UploadFileOutlined,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Card,
    CardActions,
    CardContent,
    Chip,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { type ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import Result from 'components/v2/common/Result';
import EditSignatureDialog from 'views/v2/signatures/components/editSignatureDialog';
import { SignaturePreview } from 'views/v2/signatures/components/signaturePreview';

import { ACTIONS } from 'constant';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { deleteSignature, setDefaultSignature } from 'redux/signatures/signatures.middleware';
import {
    selectSignatures,
    selectSignaturesDeleting,
    selectSignaturesUpdating,
} from 'redux/signatures/signatures.slice';
import type { AppDispatch } from 'redux/store';
import type { AgentSignature, SignatureCreationMethod } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

const METHOD_META: Record<SignatureCreationMethod, { label: string; icon: ReactElement }> = {
    typed: { label: 'Typed', icon: <TextFieldsOutlined fontSize="inherit" /> },
    drawn: { label: 'Drawn', icon: <DrawOutlined fontSize="inherit" /> },
    uploaded: { label: 'Uploaded', icon: <UploadFileOutlined fontSize="inherit" /> },
};

export default function SignatureList() {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const signatures = useSelector(selectSignatures);
    const isUpdating = useSelector(selectSignaturesUpdating);
    const isDeleting = useSelector(selectSignaturesDeleting);

    const { modalObject, setModalObject, onResetModalObj } = useModalObj<AgentSignature>();

    const handleAction = (action: ACTIONS, data: AgentSignature) => {
        switch (action) {
            case ACTIONS.EDIT:
                setModalObject((prev) => ({ ...prev, action, data }));
                break;
            case ACTIONS.DELETE:
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    data,
                    title: 'Delete signature?',
                    content:
                        'Are you sure you want to delete this signature? This action cannot be undone.',
                }));
                break;
            default:
                break;
        }
    };

    const handleSetDefault = async (signature: AgentSignature) => {
        if (signature.is_default) return;
        try {
            await dispatch(setDefaultSignature(signature.id)).unwrap();
            dispatch(setAlert({ message: 'Default signature updated', type: 'success' }));
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to set default signature'),
                    type: 'error',
                })
            );
        }
    };

    const handleConfirmDelete = async () => {
        if (!modalObject.data) return;
        try {
            setModalObject((prev) => ({ ...prev, loading: true }));
            await dispatch(deleteSignature(modalObject.data.id)).unwrap();
            dispatch(setAlert({ message: 'Signature deleted', type: 'success' }));
            onResetModalObj();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, loading: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to delete signature'),
                    type: 'error',
                })
            );
        }
    };

    if (signatures.length === 0) {
        return (
            <Result
                icon={<DrawOutlined sx={{ fontSize: 32 }} />}
                title={translate('noSignaturesYet')}
                description={translate('noSignaturesYetDescription')}
            />
        );
    }

    return (
        <Box>
            <Grid container spacing={2}>
                {signatures.map((signature) => {
                    const method = METHOD_META[signature.creation_method];
                    const title =
                        signature.signature_name || `${method?.label || 'Signature'} signature`;

                    return (
                        <Grid size={{ xs: 12, sm: 6 }} key={signature.id}>
                            <Card
                                elevation={0}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    border: '1px solid',
                                    borderColor: signature.is_default ? 'primary.main' : 'divider',
                                    borderRadius: 2,
                                    boxShadow: signature.is_default
                                        ? '0 0 0 1px rgba(99, 102, 241, 0.2)'
                                        : 'none',
                                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                    '&:hover': {
                                        transform: 'none',
                                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                                    },
                                }}
                            >
                                <CardContent sx={{ flexGrow: 1, p: 1.5, pb: 1 }}>
                                    <Stack
                                        direction="row"
                                        sx={{
                                            mb: 1,
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <Chip
                                            size="small"
                                            icon={method?.icon}
                                            label={method?.label || signature.creation_method}
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 1,
                                                fontWeight: 500,
                                                '& .MuiChip-icon': { ml: 0.75 },
                                            }}
                                        />
                                        {signature.is_default ? (
                                            <Chip
                                                size="small"
                                                label="Default"
                                                color="primary"
                                                sx={{ fontWeight: 600, borderRadius: 1 }}
                                            />
                                        ) : null}
                                    </Stack>

                                    <Box
                                        sx={{
                                            minHeight: 72,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 1.5,
                                            px: 1,
                                            py: 1,
                                            borderRadius: 1.5,
                                            // Same surface for typed / drawn / uploaded previews
                                            bgcolor: 'transparent',
                                            border: '1px dashed',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <SignaturePreview signature={signature} maxHeight={56} />
                                    </Box>

                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                        {title}
                                    </Typography>

                                    {signature.title ? (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block' }}
                                        >
                                            {signature.title}
                                        </Typography>
                                    ) : null}

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 0.5, display: 'block' }}
                                    >
                                        Created{' '}
                                        {new Date(signature.created_at).toLocaleDateString()}
                                    </Typography>
                                </CardContent>

                                <CardActions
                                    sx={{
                                        justifyContent: 'space-between',
                                        px: 1.5,
                                        pb: 1,
                                        pt: 0,
                                    }}
                                >
                                    <Tooltip
                                        title={
                                            signature.is_default
                                                ? 'Default signature'
                                                : 'Set as default'
                                        }
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleSetDefault(signature)}
                                                disabled={signature.is_default || isUpdating}
                                                color={signature.is_default ? 'primary' : 'default'}
                                            >
                                                {signature.is_default ? <Star /> : <StarBorder />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    <Stack direction="row" spacing={0.5}>
                                        <Tooltip title="Edit details">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleAction(ACTIONS.EDIT, signature)
                                                    }
                                                    disabled={isUpdating}
                                                >
                                                    <EditOutlined fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() =>
                                                        handleAction(ACTIONS.DELETE, signature)
                                                    }
                                                    disabled={isDeleting}
                                                >
                                                    <DeleteOutlined fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {modalObject.action === ACTIONS.EDIT ? (
                <EditSignatureDialog
                    signature={modalObject.data as AgentSignature}
                    onClose={onResetModalObj}
                />
            ) : null}

            {modalObject.action === ACTIONS.DELETE ? (
                <ConfirmationDialog
                    title={modalObject.title}
                    loading={modalObject.loading || isDeleting}
                    content={
                        <Stack spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                {modalObject.content}
                            </Typography>
                            {modalObject.data?.is_default ? (
                                <Alert severity="warning">
                                    This is your default signature. After deletion, set another
                                    signature as default.
                                </Alert>
                            ) : null}
                        </Stack>
                    }
                    onClose={onResetModalObj}
                    onConfirm={handleConfirmDelete}
                />
            ) : null}
        </Box>
    );
}
